import React, { useEffect } from "react";
import { useParams, Navigate, Outlet } from "react-router-dom";
import { useProjects } from "../contexts/ProjectContext";
import { LoadingScreen } from "./LoadingScreen";

/**
 * Route layout component that reads :projectSlug from the URL,
 * resolves it to a project, and selects it in the ProjectContext.
 * Renders <Outlet /> for nested child routes.
 * Redirects to "/" if no matching project is found.
 */
export const ProjectSlugGuard: React.FC = () => {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { findProjectBySlug, selectProject, isLoading, projects, activeProject } = useProjects();

  const matchingProject = projectSlug ? findProjectBySlug(projectSlug) : undefined;

  // Only sync when the URL slug or available projects change,
  // NOT when activeProject changes (avoids race with clearActiveProject).
  useEffect(() => {
    if (matchingProject) {
      localStorage.setItem("braindog_prodProjectId", JSON.stringify({ id: matchingProject.id }));
      selectProject(matchingProject.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectSlug, projects]);

  if (isLoading) return <LoadingScreen />;
  if (!matchingProject) return <Navigate to="/projects" replace />;
  if (activeProject?.id !== matchingProject.id) return <LoadingScreen />;

  return <Outlet />;
};
